import {
  collection,
  doc,
  addDoc,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from 'firebase/firestore';
import { useCallback } from 'react';
import { db } from '@/lib/firebase/config';

// Analytics types
export interface MenuViewAnalytics {
  restaurantId: string;
  viewId: string; // Unique view session ID
  timestamp: Date;
  userAgent?: string;
  referrer?: string;
  sessionDuration?: number; // in seconds
}

export interface RestaurantAnalytics {
  restaurantId: string;
  totalViews: number;
  uniqueViews: number;
  lastViewed: Date;
  dailyViews: Record<string, number>; // YYYY-MM-DD -> count
  monthlyViews: Record<string, number>; // YYYY-MM -> count
}

// Collection references
const analyticsCollection = collection(db, 'menuAnalytics');
const restaurantStatsCollection = collection(db, 'restaurantStats');

/**
 * Track a menu view for analytics
 */
export const trackMenuView = async (
  restaurantId: string,
  sessionData?: {
    userAgent?: string;
    referrer?: string;
  }
): Promise<string> => {
  try {
    const viewId = generateViewId();
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const month = today.substring(0, 7); // YYYY-MM
    
    // Create view record
    const viewData = {
      restaurantId,
      viewId,
      timestamp: serverTimestamp(),
      userAgent: sessionData?.userAgent || navigator.userAgent,
      referrer: sessionData?.referrer || document.referrer || 'direct',
      sessionStarted: serverTimestamp(),
    };

    await addDoc(analyticsCollection, viewData);

    // Update restaurant stats
    const statsDocRef = doc(restaurantStatsCollection, restaurantId);
    const statsDoc = await getDoc(statsDocRef);

    if (statsDoc.exists()) {
      // Update existing stats
      await updateDoc(statsDocRef, {
        totalViews: increment(1),
        lastViewed: serverTimestamp(),
        [`dailyViews.${today}`]: increment(1),
        [`monthlyViews.${month}`]: increment(1),
        updatedAt: serverTimestamp(),
      });
    } else {
      // Create initial stats
      await setDoc(statsDocRef, {
        restaurantId,
        totalViews: 1,
        uniqueViews: 1, // Will be calculated more accurately later
        lastViewed: serverTimestamp(),
        dailyViews: { [today]: 1 },
        monthlyViews: { [month]: 1 },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    return viewId;
  } catch (error) {
    console.error('Error tracking menu view:', error);
    // Don't throw error - analytics shouldn't break the app
    return 'error';
  }
};

/**
 * Update session duration when user leaves
 */
export const updateSessionDuration = async (
  viewId: string,
  sessionDuration: number
): Promise<void> => {
  try {
    const viewQuery = query(
      analyticsCollection,
      where('viewId', '==', viewId),
      limit(1)
    );
    
    const snapshot = await getDocs(viewQuery);
    if (!snapshot.empty) {
      const docRef = snapshot.docs[0].ref;
      await updateDoc(docRef, {
        sessionDuration,
        sessionEnded: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error('Error updating session duration:', error);
    // Don't throw error - analytics shouldn't break the app
  }
};

/**
 * Get restaurant analytics (for future dashboard)
 */export const getRestaurantAnalytics = async (
  restaurantId: string
): Promise<RestaurantAnalytics | null> => {
  try {
    const statsDocRef = doc(restaurantStatsCollection, restaurantId);
    const statsDoc = await getDoc(statsDocRef);
    
    if (statsDoc.exists()) {
      const data = statsDoc.data();
      return {
        restaurantId,
        totalViews: data.totalViews || 0,
        uniqueViews: data.uniqueViews || 0,
        lastViewed: data.lastViewed?.toDate() || new Date(),
        dailyViews: data.dailyViews || {},
        monthlyViews: data.monthlyViews || {},
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting restaurant analytics:', error);
    return null;
  }
};

/**
 * Get recent menu views for a restaurant
 */
export const getRecentMenuViews = async (
  restaurantId: string,
  limitCount: number = 100
): Promise<MenuViewAnalytics[]> => {
  try {
    const viewsQuery = query(
      analyticsCollection,
      where('restaurantId', '==', restaurantId),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(viewsQuery);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        restaurantId: data.restaurantId,
        viewId: data.viewId,
        timestamp: data.timestamp?.toDate() || new Date(),
        userAgent: data.userAgent,
        referrer: data.referrer,
        sessionDuration: data.sessionDuration,
      };
    });
  } catch (error) {
    console.error('Error getting recent menu views:', error);
    return [];
  }
};

/**
 * Generate a unique view ID
 */
const generateViewId = (): string => {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 15);
  return `view_${timestamp}_${randomStr}`;
};

/**
 * Analytics hooks for React components
 */
export const useMenuAnalytics = (restaurantId: string) => {
  const trackView = useCallback(async () => {
    if (!restaurantId) return null;
    
    return await trackMenuView(restaurantId, {
      userAgent: navigator.userAgent,
      referrer: document.referrer,
    });
  }, [restaurantId]);

  const trackSessionEnd = useCallback(async (viewId: string, startTime: number) => {
    if (!viewId || viewId === 'error') return;
    
    const sessionDuration = Math.floor((Date.now() - startTime) / 1000);
    await updateSessionDuration(viewId, sessionDuration);
  }, []);

  return {
    trackView,
    trackSessionEnd,
  };
};